"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface AvatarProps {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void;
  showDropdown?: boolean;
  dropdownItems?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ className?: string }>;
    variant?: "default" | "destructive";
    subMenu?: Array<{
      label: string;
      onClick: () => void;
      icon?: React.ComponentType<{ className?: string }>;
    }>;
  }>;
  status?: 'online' | 'away' | 'busy' | 'invisible';
  showStatusIndicator?: boolean;
}

export function Avatar({
  src,
  alt = "User avatar",
  fallback = "U",
  size = "md",
  className,
  onClick,
  showDropdown = false,
  dropdownItems = [],
  status = 'online',
  showStatusIndicator = false,
}: AvatarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);
  const [subMenuPosition, setSubMenuPosition] = useState<'left' | 'right'>('right');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sizeClasses = {
    sm: "h-8 w-8 text-sm",
    md: "h-10 w-10 text-base",
    lg: "h-12 w-12 text-lg",
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAvatarClick = () => {
    if (showDropdown) {
      setIsDropdownOpen(!isDropdownOpen);
    } else if (onClick) {
      onClick();
    }
  };

  // Calculate submenu position based on viewport
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateSubMenuPosition = (menuIndex: number) => {
    if (typeof window === 'undefined') return 'right';

    const dropdown = dropdownRef.current;
    if (!dropdown) return 'right';

    const rect = dropdown.getBoundingClientRect();
    const submenuWidth = 192; // w-48 = 192px
    const windowWidth = window.innerWidth;

    // Check if there's enough space on the right
    const spaceOnRight = windowWidth - rect.right;

    // If not enough space on right, position to left
    if (spaceOnRight < submenuWidth + 20) {
      return 'left';
    }

    return 'right';
  };

  // Update submenu position when it opens
  useEffect(() => {
    if (openSubMenu !== null) {
      const position = calculateSubMenuPosition(openSubMenu);
      setSubMenuPosition(position);
    }
  }, [openSubMenu]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
        return 'bg-yellow-500';
      case 'busy':
        return 'bg-red-500';
      case 'invisible':
        return 'bg-gray-400';
      default:
        return 'bg-green-500';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleAvatarClick}
        className={cn(
          "relative inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-600 font-medium overflow-hidden transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          sizeClasses[size],
          className
        )}
      >
        {src ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="font-semibold">{fallback}</span>
        )}
      </button>
      {/* Status Indicator */}
        {showStatusIndicator && (
          <div
            className={cn(
              "absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white flex items-center justify-center",
              getStatusColor(status)
            )}
            title={`Status: ${status}`}
          >
            {/* Inner dot for better visibility */}
            <div className="h-2 w-2 rounded-full bg-white" />
          </div>
        )}

      {/* Dropdown Menu */}
      {showDropdown && isDropdownOpen && dropdownItems.length > 0 && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {dropdownItems.map((item, index) => (
              <div key={index} className="relative">
                <button
                  onClick={() => {
                    if (item.subMenu) {
                      setOpenSubMenu(openSubMenu === index ? null : index);
                    } else {
                      item.onClick();
                      setIsDropdownOpen(false);
                    }
                  }}
                  onMouseEnter={() => {
                    // Clear any pending close timeout
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                      hoverTimeoutRef.current = null;
                    }

                    // Open submenu on hover if main dropdown is open and this item has a submenu
                    if (isDropdownOpen && item.subMenu && openSubMenu !== index) {
                      setOpenSubMenu(index);
                    }
                  }}
                  onMouseLeave={() => {
                    // Only start close timeout if we're not hovering over submenu
                    if (isDropdownOpen && item.subMenu && openSubMenu === index) {
                      hoverTimeoutRef.current = setTimeout(() => {
                        setOpenSubMenu(null);
                      }, 300); // Increased delay for better UX
                    }
                  }}
                  className={cn(
                    "w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 transition-colors",
                    item.variant === "destructive"
                      ? "text-red-600 hover:bg-red-50"
                      : "text-gray-700"
                  )}
                >
                  {item.icon && <item.icon className="h-4 w-4" />}
                  {item.label}
                  {item.subMenu && (
                    <span className="ml-auto text-xs">▶</span>
                  )}
                </button>

                {/* Sub-menu */}
                {item.subMenu && openSubMenu === index && (
                  <div
                    className={cn(
                      "absolute top-0 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50",
                      subMenuPosition === 'left' ? "right-full mr-1" : "left-full ml-1"
                    )}
                    onMouseEnter={() => {
                      // Clear any pending close timeout and keep submenu open
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                      }
                    }}
                    onMouseLeave={() => {
                      // Start close timeout when leaving submenu
                      hoverTimeoutRef.current = setTimeout(() => {
                        setOpenSubMenu(null);
                      }, 300); // Same delay as main menu
                    }}
                  >
                    <div className="py-1">
                      {item.subMenu.map((subItem, subIndex) => {
                        // Extract status from label for color coding
                        const statusMatch = subItem.label.toLowerCase().match(/(online|away|busy|invisible)/);
                        const status = statusMatch ? statusMatch[1] as 'online' | 'away' | 'busy' | 'invisible' : 'online';

                        return (
                          <button
                            key={subIndex}
                            onClick={() => {
                              subItem.onClick();
                              setIsDropdownOpen(false);
                              setOpenSubMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 transition-colors text-gray-700"
                          >
                            {subItem.icon && (
                              <div className={cn("h-4 w-4 rounded-full border border-gray-300", getStatusColor(status))}>
                                <div className="h-full w-full rounded-full bg-white scale-50" />
                              </div>
                            )}
                            {subItem.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
